import React, { useState, useEffect, useRef, useCallback } from 'react';
import Header, { BreadcrumbItem } from '../components/Header';
import ContentReader from '../components/ContentReader';
import HistoryButton from '../components/HistoryButton';
import StepHelpButton from '../components/StepHelpButton';
import HistoryModal from '../components/HistoryModal';
import { progressService, ProgressItem, JourneyProgress } from '../services/progressService';
import { InventoryService } from '../services/inventoryService';
import { useStepWorkspaceRevision } from '../context/StepWorkspaceContext';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';
import StepShareDialog from '../components/StepShareDialog';
import { useStepShareDialog } from '../hooks/useStepShareDialog';
import { withShareButton } from '../utils/stepShareUtils';
import { mergeStepActions, stepDataWithActions, buildStepDataWithActions } from '../utils/mergeStepActions';
import { perfMark, perfSummary, perfTimeAsync } from '../utils/perfTimer';
import { getStepActionSchema } from '../config/stepActionSchemas';

interface Step11Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const Step11: React.FC<Step11Props> = ({ user, onLogin, onLogout }) => {
  const stepWorkspaceRevision = useStepWorkspaceRevision();
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  
  const [quietTimePlan, setQuietTimePlan] = useState<string>('');
  const [practiced3Days, setPracticed3Days] = useState<boolean>(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [savedPlan, setSavedPlan] = useState(false);
  const [savePlanError, setSavePlanError] = useState(false);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: "Read the SAA Green Book about Step Eleven",
      description: "Learn how prayer and meditation deepen my conscious contact and guide meaningful action",
      completed: false,
      hasButton: true,
      buttonText: "Read Green Book",
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: "Read guidance on working Step Eleven",
      description: "Read practical guidance on how to work Step Eleven effectively.",
      completed: false,
      hasButton: true,
      buttonText: "Read Guidance",
      buttonAction: () => setIsGuidanceOpen(true)
    },
    {
      id: 3,
      title: "Define my daily quiet time plan and practice it",
      description: "Write out your plan for prayer and meditation, then practice it in your daily routine.",
      completed: false
    },
    {
      id: 4,
      title: "Share and review your work with your sponsor",
      description: "Share your quiet time plan with your sponsor. You can check this off when you've shared or discussed your work — using the share button is optional.",
      completed: false
    },
    {
      id: 5,
      title: "Take Step Eleven",
      description: "Sought through prayer and meditation to improve our conscious contact with God as we understood Him, praying only for knowledge of His will for us and the power to carry that out.",
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();

  const getShareContext = useCallback(
    () => ({ quietTimePlan, quietTimePracticed3Days: practiced3Days }),
    [quietTimePlan, practiced3Days]
  );
  const { openShareDialog, shareDialogProps } = useStepShareDialog(11, user, actions, getShareContext);

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: false },
    { label: '12 Steps', path: '/saa/12-steps', active: false },
    { label: 'Step Eleven', path: '/saa/12-steps/step-11', active: true }
  ];

  const loadQuietTimePlan = async () => {
    try {
      const inventoryData = await InventoryService.getInventory('step11-quiet-time-plan', '1.0');
      if (inventoryData && inventoryData.length > 0) {
        const planData = inventoryData[0];
        if (planData.plan) setQuietTimePlan(planData.plan);
        if (planData.practiced3Days) setPracticed3Days(planData.practiced3Days);
      }
    } catch (err) {
      console.error('Error loading quiet time plan:', err);
    }
  };

  const applyLoadedStepProgress = async (
    existingProgress: ProgressItem | null,
    journeyProgress: JourneyProgress | null
  ) => {
    if (journeyProgress) {
      setIsOptedOut(journeyProgress.opted_out);
    }

    if (existingProgress) {
      const savedData = existingProgress.data || {};
      setStepData(existingProgress);

      if (savedData.actions) {
        const merged = mergeStepActions(11, originalActions, savedData, !!existingProgress.completed);
        setActions(merged.actions);
        if (merged.migrationApplied) {
          await progressService.updateStepProgress({
            program: 'SAA',
            track: '12-steps',
            step_number: 11,
            step_title: 'Step Eleven',
            completed: !!existingProgress.completed,
            data: buildStepDataWithActions(merged.actions, merged.actionSchemaVersion, savedData),
          });
        }
      }
    }
  };

  const refreshStepData = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }

    setIsInitializing(true);

    try {
      perfMark('Step11', 'refreshStepData start');
      const [{ steps, journeyProgress }] = await Promise.all([
        perfTimeAsync('Step11 loadStepPagesProgress (parallel)', () =>
          progressService.loadStepPagesProgress('SAA', '12-steps', [11], { includeJourney: true })
        ),
        loadQuietTimePlan(),
      ]);

      await applyLoadedStepProgress(steps[11] ?? null, journeyProgress);
      clearError();
      perfSummary('Step Eleven page');
    } catch (err: any) {
      console.error('Error loading step progress:', err);
      handleError(err, 'Failed to load your progress. Please try refreshing the page.');
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    refreshStepData();
  }, [user, stepWorkspaceRevision]);

  const handleHistoryStepUpdated = () => {
    setIsHistoryModalOpen(false);
    if (user) {
      void refreshStepData();
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  const getCurrentActionIndex = () => {
    return actions.findIndex(action => !action.completed);
  };

  const isActionAvailable = (actionIndex: number) => {
    if (actionIndex === 0) return true;
    return actions.slice(0, actionIndex).every(a => a.completed);
  };

  // Auto-save quiet time plan
  const saveQuietTimePlan = async () => {
    if (!user) return;
    
    setSavingPlan(true);
    setSavedPlan(false);
    setSavePlanError(false);
    
    try {
      await InventoryService.saveInventory('step11-quiet-time-plan', '1.0', [{
        plan: quietTimePlan,
        practiced3Days: practiced3Days
      }]);
      setSavedPlan(true);
      setTimeout(() => setSavedPlan(false), 3000);
    } catch (err) {
      console.error('Error saving quiet time plan:', err);
      setSavePlanError(true);
    } finally {
      setSavingPlan(false);
    }
  };

  const autoSaveQuietTimePlan = () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    autoSaveTimeoutRef.current = setTimeout(() => {
      saveQuietTimePlan();
    }, 2000);
  };

  const handleActionChange = async (updatedActions: ActionItem[]) => {
    if (!user) {
      handleError(new Error('User not logged in'), 'Please log in to save your progress.');
      return;
    }
    
    try {
      // Update local state
      setActions(updatedActions);
      
      // Save to backend
      const allCompleted = updatedActions.every(action => action.completed);
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 11,
        step_title: 'Step Eleven',
        completed: allCompleted,
        data: stepDataWithActions(updatedActions, getStepActionSchema(11).actionSchemaVersion)
      });
      
      // Update step data for history modal
      if (updatedStepData && updatedStepData.step) {
        setStepData(updatedStepData.step);
      }
      
      // Update status note
      const completedCount = updatedActions.filter(a => a.completed).length;
      await progressService.updateStatus({
        program: 'SAA',
        track: '12-steps',
        status_note: `Working Step Eleven - ${completedCount} of 5 actions completed`,
        progress_milestone: allCompleted ? 'step11_completed' : 'step11_in_progress'
      });
    } catch (err: any) {
      console.error('Error updating action progress:', err);
      handleError(err, 'Failed to save progress. Please try again.');
    }
  };

  const renderAction3Content = (action: ActionItem) => {
    if (action.id !== 3) return null;

    return (
      <div className={styles.actionContentIndent}>
        <div style={{ marginBottom: 16, width: '100%' }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ 
              fontWeight: 600, 
              color: '#333', 
              fontSize: '14px',
              display: 'block',
              marginBottom: '8px'
            }}>
              My Plan for Daily Quiet Time
            </label>
            <textarea
              value={quietTimePlan}
              onChange={(e) => {
                setQuietTimePlan(e.target.value);
                autoSaveQuietTimePlan();
              }}
              onBlur={saveQuietTimePlan}
              placeholder="Write out your plan for establishing or continuing a daily quiet time practice..."
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '12px',
                border: '1px solid #ccc',
                borderRadius: 4,
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={practiced3Days}
                onChange={(e) => {
                  setPracticed3Days(e.target.checked);
                  saveQuietTimePlan();
                }}
                style={{ width: '16px', height: '16px' }}
              />
              <span style={{ fontWeight: 500, color: '#333' }}>
                Practiced My Plan for 3+ days
              </span>
            </label>
          </div>
          
          <div style={{ 
            textAlign: 'right', 
            minHeight: 24,
            fontSize: '14px',
            fontWeight: 500
          }}>
            {savingPlan && (
              <span style={{ color: '#6c757d' }}>
                Saving...
              </span>
            )}
            {savedPlan && (
              <span style={{ color: '#28a745' }}>
                ✓ All changes saved
              </span>
            )}
            {savePlanError && (
              <span style={{ color: '#dc3545' }}>
                ⚠ Save failed. Please try again.
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Check if all actions are completed
  const allCompleted = actions.every(action => action.completed);

  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>Step Eleven</h1>
            <StepHelpButton stepKey="app" />
            {stepData && (
              <HistoryButton
                stepNumber={11}
                stepTitle="Step Eleven"
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>
            Sought through prayer and meditation to improve our conscious contact with God as we understood Him, praying only for knowledge of His will for us and the power to carry that out.
          </p>
        </div>

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>Refresh Page</button>
              </>
            )}
          </div>
        )}

        {!user && !isInitializing && !errorMessage && (
          <StepLoginPrompt onLogin={onLogin} />
        )}

        {user && isInitializing && !errorMessage && (
          <div className={styles.loadingMessage}>
            <p>Loading your progress...</p>
          </div>
        )}

        {user && !isInitializing && !errorMessage && (
          <div className={styles.actionsContainer}>
            <h2 className={styles.actionsTitle}>Actions to Complete Step Eleven</h2>
            <p className={styles.actionsDescription}>
              Complete each action in order. Check the box when you've finished each action.
            </p>
            
            <StepActions
              actions={withShareButton(11, actions, openShareDialog)}
              onActionChange={handleActionChange}
              stepNumber={11}
              renderActionContent={renderAction3Content}
            />

            {allCompleted && (
              <>
                <div className={styles.completionMessage}>
                  <h3>🎉 Congratulations!</h3>
                  <p>You have completed all actions for Step Eleven and can move on to Step Twelve.</p>
                </div>
                <div className={styles.stepNavigation}>
                  <div className={styles.navigationLinks}>
                    <a href="/saa/12-steps/step-10" className={styles.prevStepLink}>
                      {'<<'} Step Ten
                    </a>
                    <span></span>
                    <a href="/saa/12-steps/step-12" className={styles.nextStepLink}>
                      Step Twelve {'>>'}
                    </a>
                  </div>
                </div>
              </>
            )}

                    {!allCompleted && (
          <div className={styles.stepNavigation}>
            <div className={styles.navigationLinks}>
              <a href="/saa/12-steps/step-10" className={styles.prevStepLink}>
                {'<<'} Step Ten
              </a>
              <span></span>
              <a href="/saa/12-steps/step-12" className={styles.nextStepLink}>
                Step Twelve {'>>'}
              </a>
            </div>
          </div>
        )}
          </div>
        )}
      </main>

      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-eleven start>>"
        endTag="<<step-eleven end>>"
      />

      {/* ContentReader for How to Work Step Eleven */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_11_Guidance.md"
        startTag="<<working-step-eleven start>>"
        endTag="<<working-step-eleven end>>"
        title="Guidance on working Step Eleven"
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={11}
        stepTitle="Step Eleven"
        stepData={stepData}
        onStepUpdated={handleHistoryStepUpdated}
      />

      <StepShareDialog {...shareDialogProps} />
    </div>
  );
};

export default Step11; 